"use client";

import axios, { AxiosResponse } from "axios";
import jwtDecode, { JwtPayload } from "jwt-decode";
import { createContext, type ReactNode, useEffect, useState } from "react";
import { deleteCookie, setCookie } from "cookies-next/client";
import { fetcherFactory } from "@/utils/Fetcher";
import { isCurrentTokenValid } from "@/utils/isCurrentTokenValid";

interface TokenContextType {
  accessToken: string | null;
  getDecodedData: () => JwtPayload | null;
  isLoggedIn: () => Promise<boolean>;
  setAuthTokens: (accessToken: string, refreshToken: string) => void;
  resetAuthTokens: () => void;
  authenticatedFetcher: ReturnType<typeof fetcherFactory>;
  decodedData: JwtPayload | null; // Deprecated
}

const TokenContext = createContext<TokenContextType | undefined>(undefined);

interface TokenProviderProps {
  children: ReactNode;
  initialAccessToken?: string | null;
  initialRefreshToken?: string | null;
}

export const TokenProvider = ({ children, initialAccessToken, initialRefreshToken }: TokenProviderProps) => {

  if (!isCurrentTokenValid(initialAccessToken)) {
    initialAccessToken = null;
  }

  if (!isCurrentTokenValid(initialRefreshToken)) {
    initialRefreshToken = null;
  }

  const [accessToken, setAccessToken] = useState<string | null>(initialAccessToken || null);

  useEffect(() => {
    isLoggedIn();
  }, []);

  const setAuthTokens = (accessToken, refreshToken) => {
    setCookie('access_token', accessToken);
    setAccessToken(accessToken);
  };

  const resetAuthTokens = () => {
    deleteCookie('access_token');
    deleteCookie('refresh_token');
    setAccessToken(null);
  };

  let promisedAccessTokenRequest: Promise<AxiosResponse<any, any>> | undefined;

  const isLoggedIn = async () => {

    if (isCurrentTokenValid(accessToken)) {
      return true;
    }

    return false;
  };

  // const authenticatedFetcher = fetcherFactory(requestInit);
  const authenticatedFetcherFactory = (requestInit: RequestInit = {}) => {
    return fetcherFactory({
      ...requestInit,
      headers: {
        ...requestInit.headers,
        Authorization: `Bearer ${accessToken}`
      }
    });
  };

  const getDecodedData = () => {
    if (!isCurrentTokenValid(accessToken)) return null;
    return jwtDecode<JwtPayload>(accessToken!); // Added non-null assertion
  };

  const authenticatedFetcher = authenticatedFetcherFactory();

  return (
    <TokenContext.Provider
      value={{
        accessToken,
        getDecodedData,
        isLoggedIn,
        setAuthTokens,
        resetAuthTokens,
        authenticatedFetcher,

        // deprecated
        decodedData: getDecodedData() // Fixed assignment
      }}
    >
      {children}
    </TokenContext.Provider>
  );
};

export default TokenContext;
